import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import EmptyState from '../components/EmptyState';
import { useNavigate, useLocation } from '../routing';
import { creditsApi, formatCurrencyWhole } from '../utils/api';
import { track, EVENTS } from '../utils/analytics';
import { toast } from '../components/Toast';
import CreditsFilterBar from '../components/CreditsFilterBar';
import CreditsViewTabs, { consumeViewEntry } from '../components/CreditsViewTabs';
import CreditsTable from '../components/CreditsTable';
import CreditsMetricsRow from '../components/CreditsMetricsRow';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { SORT_ACCESSORS, buildCreditGroups, readSavedCreditsFilters, writeSavedCreditsFilters, initialCreditsRange, creditsDefaultRange, CREDITS_DEFAULT_PRESET, CREDIT_STATUS } from '../utils/creditsShared';
import { vatMultiplier } from '../utils/money';
import { useIsMobile } from '../utils/useIsMobile';
// Default date range = the last 90 days, on every entry (owner, 28/09/2026) —
// see initialCreditsRange in utils/creditsShared.js.
import { periodLabel, PRESETS_ANALYTIC } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import Icon from '../components/Icon';
import ConfirmModal from '../components/ConfirmModal';
import CreditReceiveModal from '../components/CreditReceiveModal';
import CreditCloseReasonPicker from '../components/CreditCloseReasonPicker';
import { CREDIT_CLOSE_REASONS } from '../utils/creditCloseReasons';
import CreditDetailSheet from '../components/CreditDetailSheet';
import InlineLoadError from '../components/InlineLoadError';
import MobileSort from '../components/MobileSort';
import { useSortable } from '../utils/useSortable';
import PageViewAsMenu from '../components/ViewAsMenu';
import HqOnlyNotice from '../components/HqOnlyNotice';

// The default /credits view — credits still owed, status permanently locked
// to 'פתוח' (server-side, via the API's status param). This is the "still
// owed" half of CreditsViewTabs; /credits/history (CreditsHistoryPage) is
// the other. Splitting into two routes means this page's cards and table can
// never disagree with each other — every row here IS open, so "X open" and
// "X owed" describe the exact same rows, unlike the single-page-with-a-
// status-filter design this replaced.
const OPEN_STATUS = CREDIT_STATUS.OPEN;

export default function CreditsOpenPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { vatRate } = useSettings();
  // Manager-area capability: admin + hq_admin + manager may act on credits. Viewers
  // get a read-only screen (the server also blocks them via requireWrite).
  const { canEdit, user, restaurantId } = useAuth();
  // Restore the sticky supplier filter saved this session, shared with
  // /credits/history via the same sessionStorage key. The period comes back only when
  // returning to this view (initialCreditsRange).
  const saved = useMemo(() => readSavedCreditsFilters(user?.id, restaurantId), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [credits,      setCredits]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(false);
  const [nameFilter,   setNameFilter]   = useState(saved.nameFilter ?? '');
  // Always opens on the default — see initialCreditsRange for the two exceptions.
  const initialRange = useMemo(() => initialCreditsRange(saved, location), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [dateFrom,     setDateFrom]     = useState(initialRange.from);
  const [dateTo,       setDateTo]       = useState(initialRange.to);
  // Rolls a preset forward when the tab comes back after the day changed, and
  // is the key the period is saved under (utils/usePresetPeriod.js).
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, (f, t) => { setDateFrom(f); setDateTo(t); });
  const [modal,        setModal]        = useState(null);
  // Mobile credit detail sheet — the credit whose row was tapped.
  const [creditSheet,  setCreditSheet]  = useState(null);
  // Bulk "mark as received" — an opt-in selection mode, same shape as the
  // multi-payment mode on Suppliers. Off by default so the ordinary
  // one-credit-at-a-time flow keeps its plain rows. Every row on this page is
  // already 'פתוח', so — unlike the pre-split page — selection never needs to
  // exclude or dim any row.
  const [selectMode,   setSelectMode]   = useState(false);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [bulkRunning,  setBulkRunning]  = useState(false);
  // Replaces window.confirm for cancel / status flips: { title, message,
  // detail, tone, confirmLabel, needsReason, onConfirm } or null.
  const [confirmState, setConfirmState] = useState(null);
  // The close-reason chips shown INSIDE a `needsReason` confirm dialog. Held on
  // the page rather than inside confirmState so picking a chip doesn't rebuild
  // the dialog object (and with it the onConfirm closure) on every tap.
  const [closeReason, setCloseReason] = useState('');
  const [closeNote,   setCloseNote]   = useState('');
  // Every confirm opens with the chips blank — carrying the previous credit's
  // answer over would be the app pre-filling an audit field on the user's behalf.
  const openConfirm = (state) => { setCloseReason(''); setCloseNote(''); setConfirmState(state); };
  // The table-level ⋮ (currently just the bulk-selection entry point).
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  // 720 matches the filter-bar breakpoint. It no longer moves the TABS (those
  // dock inside the card at every breakpoint now) — only the metrics row, which
  // is handed to CreditsFilterBar on mobile so it sits above the sticky wrap.
  const isFilterMobile = useIsMobile();

  // Token guard against out-of-order responses. Mutations call load()
  // imperatively (not from an effect, so there is no cleanup to lean on),
  // and two quick actions — cancel one credit, immediately receive
  // another — put two loads in flight. Without this, the SLOWER one wins
  // whenever it lands last and repaints the table with pre-mutation rows.
  const loadToken = useRef(0);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await creditsApi.getAll({
        status: OPEN_STATUS,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (token !== loadToken.current) return;   // superseded
      setCredits(r.data);
    } catch {
      // Without this the rejection escapes (unhandled) AND the render
      // falls through to EmptyState — telling the user they have no
      // credits when the truth is the request failed.
      if (token !== loadToken.current) return;
      setLoadError(true);
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // CreditsMetricsRow owns its own request, so `load()` alone refreshes the
  // table while leaving the cards on pre-mutation totals. EVERY mutation must
  // go through this instead — bumping the key is what re-runs the cards'
  // fetch. Two paths deliberately call `load()` bare instead: mount (the
  // metrics row fetches on its own first render, so bumping would duplicate
  // it) and the table's retry button (a failed load is not a mutation, and
  // the metrics row carries its own independent retry).
  const [metricsKey, setMetricsKey] = useState(0);
  const reloadAll = useCallback(() => {
    load();
    setMetricsKey(k => k + 1);
  }, [load]);

  useEffect(() => {
    track(EVENTS.CREDIT_VIEW_SHOWN, { view: 'open', entry: consumeViewEntry() });
  }, []);

  // Leaving selection mode always drops the selection — a stale selection that
  // survived a filter change could target credits no longer on screen.
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);
  useEffect(() => { exitSelectMode(); }, [dateFrom, dateTo, nameFilter, exitSelectMode]);

  // Persist the supplier + date filters so they survive the round trip to a
  // document view and back, and carry over to /credits/history.
  useEffect(() => {
    if (!user?.id) return;
    writeSavedCreditsFilters(user.id, restaurantId, { locKey: location.key, nameFilter, dateFrom, dateTo, presetKey });
  }, [user?.id, restaurantId, location.key, nameFilter, dateFrom, dateTo, presetKey]);

  // "Clear all" returns to the page default (per spec §6 — Cleared = Default,
  // not Empty). Empty (no date range at all) is reachable via the date
  // popover's "נקה טווח" button; there is no "כל הזמן" preset.
  const clearFilters = () => {
    const fresh = creditsDefaultRange();
    setNameFilter('');
    setDateFrom(fresh.from);
    setDateTo(fresh.to);
  };

  const openReceiveModal = async (credit) => {
    const r = await creditsApi.getCandidateDocs(credit.supplier_id);
    setModal({ type: 'receive', credit, candidateDocs: r.data, selectedDocId: '', reason: '', note: '' });
  };

  // Closing a credit is a money-moving write (it restores the item and
  // recomputes the source document's totals), so the server records WHO — and,
  // when no credit document is linked, demands a reason. The modal already
  // blocks confirming without one; these fields are simply passed along, and are
  // ignored server-side once a document is picked.
  const handleReceive = async () => {
    try {
      await creditsApi.update(modal.credit.id, {
        status: CREDIT_STATUS.RECEIVED,
        linked_credit_doc_id: modal.selectedDocId || null,
        closed_reason: modal.selectedDocId ? null : (modal.reason || null),
        closed_note:   modal.selectedDocId ? null : (modal.note || null),
      });
      track(EVENTS.CREDIT_STATUS_CHANGED, {
        to_status: CREDIT_STATUS.RECEIVED,
        linked_to_doc: !!modal.selectedDocId,
        close_reason: modal.selectedDocId ? null : (modal.reason || null),
      });
      toast('הזיכוי סומן כהתקבל');
      setModal(null);
      reloadAll();
    } catch { toast('שגיאה', 'error'); }
  };

  // One line of context under every confirm prompt, so the user is confirming
  // against the record rather than against a bare question.
  const creditDetail = (c) =>
    `${c.supplier_name} · ${c.item_description || '—'} · ${formatCurrencyWhole(c.credit_total)} (ללא מע״מ)`;

  // Cancelling CLOSES the credit — it restores the item and recomputes the
  // document exactly as receiving does — and it never carries a credit document,
  // so it always needs a reason. `needsReason` puts the chips inside the confirm
  // dialog and keeps its button unusable until one is picked.
  const handleCancel = (credit) => openConfirm({
    title: 'ביטול זיכוי',
    message: `לבטל את הזיכוי על "${credit.item_description}"? הזיכוי יפסיק להיספר בסכומים.`,
    detail: creditDetail(credit),
    tone: 'danger',
    confirmLabel: 'בטל זיכוי',
    needsReason: true,
    reasons: CREDIT_CLOSE_REASONS.filter(r => r.key !== 'received_no_doc'),
    reasonLabel: 'למה מבטלים? (חובה)',
    onConfirm: async ({ reason, note }) => {
      setConfirmState(null);
      try {
        await creditsApi.update(credit.id, {
          status: CREDIT_STATUS.CANCELLED, closed_reason: reason, closed_note: note || null,
        });
        track(EVENTS.CREDIT_STATUS_CHANGED, {
          to_status: CREDIT_STATUS.CANCELLED, linked_to_doc: false, close_reason: reason,
        });
        toast('הזיכוי בוטל');
        reloadAll();
      } catch { toast('שגיאה', 'error'); }
    },
  });

  // Bulk mark-as-received. Deliberately does NOT offer the linked-credit-doc
  // picker that the single-credit flow does: one document rarely covers a
  // hand-picked set, and forcing a per-credit choice would rebuild the modal
  // the bulk path exists to avoid. Anyone who needs the link can still do that
  // credit singly. Sequential, not Promise.all — better-sqlite3 is synchronous
  // and a burst of parallel writes buys nothing.
  //
  // Because it never links a document, every row it closes needs a reason — but
  // ONE reason for the whole batch, asked once in the confirm dialog. Asking per
  // row would rebuild exactly the per-credit modal this path exists to avoid,
  // and a batch is a batch precisely because the same thing happened to all of
  // it.
  const runBulkReceive = async ({ reason, note }) => {
    setConfirmState(null);
    const ids = [...selectedIds];
    setBulkRunning(true);
    let ok = 0, failed = 0;
    for (const id of ids) {
      try {
        await creditsApi.update(id, {
          status: CREDIT_STATUS.RECEIVED, closed_reason: reason, closed_note: note || null,
        });
        ok++;
      }
      catch { failed++; }
    }
    setBulkRunning(false);
    track(EVENTS.CREDIT_STATUS_CHANGED, {
      to_status: CREDIT_STATUS.RECEIVED, linked_to_doc: false, mode: 'bulk', bulk_count: ok,
      close_reason: reason,
    });
    if (failed) toast(`${ok} זיכויים סומנו כהתקבל, ${failed} נכשלו`, 'error');
    else        toast(`${ok} זיכויים סומנו כהתקבל`);
    exitSelectMode();
    reloadAll();
  };

  // Status is already server-locked to 'פתוח' — only the name search narrows
  // the rows further, client-side.
  const filtered = useMemo(
    () => credits.filter(c => !nameFilter || c.supplier_name.includes(nameFilter)),
    [credits, nameFilter],
  );
  // By preset key, so a default rolled forward by usePresetPeriod stays default.
  const isDefaultDate = presetKey === CREDITS_DEFAULT_PRESET;
  const hasFilters = Boolean(nameFilter) || !isDefaultDate;
  const vatMult = vatMultiplier(vatRate);

  // The always-visible summary — open/received/cancelled money + open count,
  // independent of this page's own status-locked table. One element used in
  // exactly one of two spots (desktop vs mobile) below; see CreditsMetricsRow.
  const metricsRow = <CreditsMetricsRow dateFrom={dateFrom} dateTo={dateTo} nameFilter={nameFilter} refreshKey={metricsKey} />;

  // One sort state shared by every group's table (and by the mobile sort
  // sheet). Sorting is applied WITHIN a group — the kind partition is the
  // page's primary structure and a sort must not dissolve it.
  const { sorted, sort, setSortTo, indicator: sortInd, headerProps: sortableTh } =
    useSortable(filtered, SORT_ACCESSORS);
  const groups = useMemo(() => buildCreditGroups(sorted, vatMult), [sorted, vatMult]);

  const hasRows = filtered.length > 0;

  // The table's one standing control at rest — bulk selection is an
  // occasional task, so it's a ⋮ rather than a permanent button. It rides the
  // tabs row at EVERY breakpoint now (CreditsViewTabs' `trailing` slot) — it
  // used to need its own line on mobile, back when the tabs lived up in the
  // filter bar instead of inside this card.
  const tableMenuNode = canEdit && hasRows && (
    <div className="page-menu">
      <button
        className="page-menu-trigger"
        aria-label="פעולות על הטבלה"
        aria-expanded={tableMenuOpen}
        onClick={() => setTableMenuOpen(o => !o)}
      >⋮</button>
      {tableMenuOpen && (
        <>
          <div className="menu-backdrop" onClick={() => setTableMenuOpen(false)} />
          <div className="page-menu-panel">
            <button className="page-menu-item"
              onClick={() => { setTableMenuOpen(false); setSelectMode(true); }}
              title="סמני כמה זיכויים וסמני אותם כהתקבל יחד">
              <Icon name="check" size={15} /> סימון מרובה
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Every row here is 'פתוח' — the only transitions available are receiving
  // or cancelling. Returned as the fragment CreditsTable slots in after its
  // own always-present "צפה במסמך" item.
  const renderRowActions = (c, closeMenu, menuBtn) => (
    <>
      <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--success-ink)', fontWeight: 600 }}
        onClick={() => { closeMenu(); openReceiveModal(c); }}>
        <Icon name="check" size={16} strokeWidth={1.6} /> סמן כהתקבל
      </button>
      <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--danger-ink)' }}
        onClick={() => { closeMenu(); handleCancel(c); }}>
        <Icon name="close" size={16} strokeWidth={1.6} /> בטל זיכוי
      </button>
    </>
  );

  return (
    <div>
      {/* ייצוא rides the title line, not the card: it acts on the whole
          filtered screen rather than on the table's selection. (The ⋮ that
          does act on the selection lives on the tabs row — see below.) */}
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">זיכויים</h1>
          <HqOnlyNotice money={formatCurrencyWhole} amount={loadError ? null : filtered.filter(c => c.hq_only).reduce((n, c) => n + (c.credit_total || 0), 0)} pending={loading} basis="זיכויים פתוחים בתקופה (ללא מע״מ)" />
        </div>
        <div className="page-title-actions">
          <button className="btn btn-sm btn-excel"
            onClick={() => creditsApi.exportExcel({
              status: OPEN_STATUS,
              date_from: dateFrom || undefined,
              date_to: dateTo || undefined,
              name: nameFilter || undefined,
            })}
            title="ייצוא לאקסל">
            <Icon name="spreadsheet" size={14} />
            ייצוא
          </button>
          <PageViewAsMenu />
        </div>
      </div>

      <CreditsFilterBar
        nameFilter={nameFilter} setNameFilter={setNameFilter}
        dateFrom={dateFrom}     dateTo={dateTo}
        setDateRange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        onClearAll={clearFilters}
        metricsRow={isFilterMobile ? metricsRow : null}
        sortControl={
          <MobileSort
            options={[
              { key: 'date',     label: 'תאריך מסמך' },
              { key: 'total',    label: 'סכום זיכוי' },
              { key: 'supplier', label: 'שם ספק' },
            ]}
            sort={sort}
            onChange={setSortTo}
          />
        }
      />

      {/* Desktop: rendered once here, right after the filter bar and before
          the tab-headed card. Mobile fills the same slot via
          CreditsFilterBar's metricsRow prop above (right after the date bar,
          before the tabs) — `metricsRow` is one element reference used in
          exactly one of the two places at a time. */}
      {!isFilterMobile && metricsRow}

      <div className="card">
        {/* The card's own header row, on EVERY breakpoint — same placement as
            /suppliers. Rendered INSIDE the card because a tab sitting on top of
            a borderless, shadowed card can't be made to look joined to it. The ⋮
            rides the SAME row, at its far (top-left) end — CreditsViewTabs'
            `trailing` slot — rather than a separate line below, so the table's
            one standing control sits with the control that already owns this
            row. This used to be desktop-only, with mobile getting a segmented
            pill up in the filter bar; the shared CSS now draws one underline bar
            at both sizes, so there is nothing left to switch. */}
        <CreditsViewTabs active="open" trailing={!selectMode ? tableMenuNode : null} />

        {/* Selection bar (count + confirm + exit). Only while selecting: the ⋮
            now rides the tabs row above at every breakpoint, so this no longer
            doubles as mobile's home for it. The tabs row drops its ⋮ while this
            is open, so the control never appears twice. */}
        {selectMode && (
          <div className="action-row">
            {(
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                  נבחרו {selectedIds.size}
                </span>
                <button className="btn btn-sm btn-success"
                  disabled={selectedIds.size === 0 || bulkRunning}
                  onClick={() => openConfirm({
                    title: 'סימון זיכויים כהתקבל',
                    message: `לסמן ${selectedIds.size} זיכויים כ"התקבל"?`,
                    detail: 'הזיכויים לא יקושרו לחשבונית זיכוי. לקישור למסמך, סמני כל זיכוי בנפרד.',
                    confirmLabel: 'סמן כהתקבל',
                    // One reason for the batch — see runBulkReceive.
                    needsReason: true,
                    onConfirm: runBulkReceive,
                  })}>
                  <Icon name="check" size={15} /> {bulkRunning ? 'מעדכן…' : 'סמן כהתקבל'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={exitSelectMode} disabled={bulkRunning}>
                  ביטול
                </button>
              </div>
            )}
            <div className="spacer" />
          </div>
        )}

        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את הזיכויים" onRetry={load} />
        ) : filtered.length === 0 ? (
          <EmptyState compact={hasFilters} message={hasFilters ? `לא נמצאו זיכויים פתוחים ב${periodLabel(dateFrom, dateTo)} לפי הסינון שנבחר` : 'אין זיכויים פתוחים'}>
            {/* The cards above are period-wide and ignore this tab's status
                lock, so they can show this supplier's money while the table
                below is empty — the rows are on the other tab. Say so, and
                offer the trip; nameFilter persists across both routes. */}
            {nameFilter && (
              <button className="empty-action" onClick={() => navigate('/credits/history', { state: { keepPeriodFrom: location.key } })}>
                חפש את "{nameFilter}" בהיסטוריית הזיכויים
              </button>
            )}
          </EmptyState>
        ) : (
          <CreditsTable
            groups={groups}
            canEdit={canEdit}
            selectMode={selectMode}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            renderRowActions={renderRowActions}
            vatMult={vatMult}
            sortableTh={sortableTh}
            sortInd={sortInd}
            onOpenDocument={(id) => navigate(`/document/${id}`)}
            onRowClick={setCreditSheet}
          />
        )}
      </div>

      {/* Mobile credit detail sheet. Every row here is 'פתוח', so no status
          row — it would read identically on every credit. */}
      {creditSheet && (
        <CreditDetailSheet
          credit={creditSheet}
          onClose={() => setCreditSheet(null)}
          vatMult={vatMult}
          renderActions={(c, closeThen) => (
            <>
              {canEdit && (
                <button className="btn btn-primary" onClick={closeThen(() => openReceiveModal(c))}>
                  <Icon name="check" size={16} /> סמן כהתקבל
                </button>
              )}
              <button className="btn btn-secondary" onClick={closeThen(() => navigate(`/document/${c.document_id}`))}>
                <Icon name="eye" size={16} /> צפה במסמך
              </button>
              {canEdit && (
                <button className="btn btn-danger" onClick={closeThen(() => handleCancel(c))}>
                  <Icon name="close" size={16} /> בטל זיכוי
                </button>
              )}
            </>
          )}
        />
      )}

      {confirmState && (
        <ConfirmModal
          {...confirmState}
          // After the spread, so they win over the raw values in confirmState:
          // onConfirm is handed the chip answer, and the button stays unusable
          // until there is one (the server would reject it with a 400, and a
          // dialog that lets you press אישור and then fails teaches distrust).
          confirmDisabled={Boolean(confirmState.needsReason) && !closeReason}
          onConfirm={() => confirmState.onConfirm({ reason: closeReason, note: closeNote })}
          onCancel={() => setConfirmState(null)}
        >
          {confirmState.needsReason && (
            <CreditCloseReasonPicker
              value={closeReason} note={closeNote}
              onChange={setCloseReason} onNoteChange={setCloseNote}
              // Cancelling drops 'התקבל זיכוי בפועל': a credit that WAS received
              // is marked התקבל, never cancelled, so offering it here only
              // invites a tap that records the opposite of what happened.
              reasons={confirmState.reasons || CREDIT_CLOSE_REASONS}
              label={confirmState.reasonLabel || undefined}
            />
          )}
        </ConfirmModal>
      )}

      {modal?.type === 'receive' && (
        <CreditReceiveModal
          modal={modal}
          onSelectDoc={(id) => setModal(m => ({ ...m, selectedDocId: id }))}
          onSetReason={(key) => setModal(m => ({ ...m, reason: key }))}
          onSetNote={(text) => setModal(m => ({ ...m, note: text }))}
          onConfirm={handleReceive}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
